import { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faCheckCircle,
  faSpinner,
  faSearch,
  faInfoCircle,
  faLink,
  faBuilding,
} from '@fortawesome/free-solid-svg-icons';
import ClientService from '@frontend/AppSettings/services/ClientService';
import AgentsService from '@frontend/AppSettings/services/AgentsService';
import OrganizationService from '@frontend/AppSettings/services/OrganizationService';
import toastService from '@frontend/AppSettings/services/ToastService';
import { useOrganizationAnchor } from '../../OrganizationAnchorContext';
import { getInitials } from '../../../Components/utils';
import './LinkExistingContactModal.css';

interface LinkExistingContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Company name of the organization the contacts will be linked to. */
  companyName: string;
  /** Called after at least one contact has been successfully linked. */
  onContactsLinked?: () => void;
}

interface CandidateContact {
  id: string;
  source: 'client' | 'agent';
  firstName: string;
  lastName: string;
  fullName: string;
  title: string;
  email: string;
  phone: string;
  address: string;
  currentCompany: string;
  /** Full original record so we can preserve unrelated fields on update. */
  raw: any;
}

const buildCandidate = (record: any, source: 'client' | 'agent'): CandidateContact => {
  const id = source === 'client' ? String(record.ClientID ?? '') : String(record.AgentID ?? '');
  const firstName = record.FirstName || '';
  const lastName = record.LastName || '';
  const fullName = `${firstName} ${lastName}`.trim();
  return {
    id,
    source,
    firstName,
    lastName,
    fullName,
    title: record.Title || '',
    email: record.Email || '',
    phone: record.Phone || '',
    address: record.Address || '',
    currentCompany: record.Company || '',
    raw: record,
  };
};

const LinkExistingContactModal: React.FC<LinkExistingContactModalProps> = ({
  isOpen,
  onClose,
  companyName,
  onContactsLinked,
}) => {
  const queryClient = useQueryClient();
  // Non-null only on a REAL organization's detail view (P2 Build J) — flips client
  // linking from the legacy PUT-the-whole-client to the join-table POST.
  const organizationAnchor = useOrganizationAnchor();
  const [candidates, setCandidates] = useState<CandidateContact[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const fetchCandidates = async () => {
      setLoading(true);
      setError('');
      try {
        const [clients, agents] = await Promise.all([
          ClientService.getClients(),
          AgentsService.getAgents(),
        ]);

        const targetCompany = (companyName || '').trim().toLowerCase();
        const list: CandidateContact[] = [];

        (clients || []).forEach((c: any) => {
          const candidate = buildCandidate(c, 'client');
          if (!candidate.fullName) return;
          // Skip people already at this organization.
          if ((candidate.currentCompany || '').trim().toLowerCase() === targetCompany) return;
          list.push(candidate);
        });

        (agents || []).forEach((a: any) => {
          const candidate = buildCandidate(a, 'agent');
          if (!candidate.fullName) return;
          if ((candidate.currentCompany || '').trim().toLowerCase() === targetCompany) return;
          list.push(candidate);
        });

        // Sort: people without a company first (orphans), then alphabetical
        list.sort((a, b) => {
          const aHasCompany = a.currentCompany ? 1 : 0;
          const bHasCompany = b.currentCompany ? 1 : 0;
          if (aHasCompany !== bHasCompany) return aHasCompany - bHasCompany;
          return a.fullName.localeCompare(b.fullName);
        });

        if (!cancelled) {
          setCandidates(list);
        }
      } catch (err: any) {
        console.error('Error loading existing contacts:', err);
        if (!cancelled) {
          setError(err?.message || 'Failed to load existing contacts.');
          setCandidates([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCandidates();
    return () => {
      cancelled = true;
    };
  }, [isOpen, companyName]);

  // Reset selection / search whenever the modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setSelectedIds(new Set());
      setError('');
    }
  }, [isOpen]);

  const filteredCandidates = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) =>
      c.fullName.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      c.title.toLowerCase().includes(q) ||
      c.currentCompany.toLowerCase().includes(q)
    );
  }, [candidates, searchTerm]);

  const allFilteredKeys = useMemo(
    () => filteredCandidates.map((c) => `${c.source}:${c.id}`),
    [filteredCandidates]
  );

  const allFilteredSelected =
    allFilteredKeys.length > 0 && allFilteredKeys.every((k) => selectedIds.has(k));

  const toggleOne = (key: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        allFilteredKeys.forEach((k) => next.delete(k));
      } else {
        allFilteredKeys.forEach((k) => next.add(k));
      }
      return next;
    });
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!companyName) {
      setError('This organization is missing a company name; cannot link contacts.');
      return;
    }
    if (selectedIds.size === 0) {
      setError('Select at least one person to link.');
      return;
    }

    setSubmitting(true);
    setError('');

    const selected = candidates.filter((c) => selectedIds.has(`${c.source}:${c.id}`));
    let successCount = 0;
    const failures: string[] = [];

    for (const candidate of selected) {
      // Preserve all known fields; only Company is being changed.
      const payload = {
        FirstName: candidate.firstName,
        LastName: candidate.lastName,
        Email: candidate.email,
        Phone: candidate.phone,
        Title: candidate.title,
        Address: candidate.address,
        Company: companyName,
      };

      try {
        let ok = false;
        if (candidate.source === 'client' && organizationAnchor) {
          // P2 Build J: linking a client to a REAL organization is a join-table POST —
          // the PUT-the-whole-client path below stays only for legacy string-groups
          // (and agents, which have no org records yet — decision 7).
          await OrganizationService.addMember(
            organizationAnchor.organization.id,
            candidate.id
          );
          ok = true;
        } else if (candidate.source === 'client') {
          ok = await ClientService.updateClient(candidate.id, payload);
        } else {
          ok = await AgentsService.updateAgent(candidate.id, payload);
        }
        if (ok) {
          successCount += 1;
        } else {
          failures.push(candidate.fullName || candidate.id);
        }
      } catch (err) {
        console.error(`Failed to link ${candidate.fullName}:`, err);
        failures.push(candidate.fullName || candidate.id);
      }
    }

    setSubmitting(false);

    if (successCount > 0) {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['Clients', 'orbit'] }),
        queryClient.invalidateQueries({ queryKey: ['Agents', 'orbit'] }),
      ]);
    }

    if (failures.length === 0) {
      toastService.success(
        successCount === 1
          ? `Linked 1 contact to ${companyName}.`
          : `Linked ${successCount} contacts to ${companyName}.`
      );
      if (onContactsLinked) onContactsLinked();
      onClose();
      return;
    }

    if (successCount > 0) {
      toastService.success(`Linked ${successCount} contact${successCount === 1 ? '' : 's'}.`);
      setError(`Could not link: ${failures.join(', ')}.`);
      if (onContactsLinked) onContactsLinked();
    } else {
      toastService.error('Failed to link selected contacts.');
      setError(`Could not link: ${failures.join(', ')}.`);
    }
  };

  if (!isOpen) return null;

  const selectedCount = selectedIds.size;

  return (
    <div className="link-existing-overlay" onClick={handleClose}>
      <div className="link-existing-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="link-existing-header">
          <div className="link-existing-header-left">
            <div className="link-existing-title-row">
              <div className="link-existing-header-icon">
                <FontAwesomeIcon icon={faLink} />
              </div>
              <div className="link-existing-header-text">
                <h2 className="link-existing-title">Link Existing Contacts</h2>
                <p className="link-existing-subtitle">
                  Attach people already in your CRM to{' '}
                  <strong>{companyName || 'this organization'}</strong>
                </p>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="link-existing-close"
            onClick={handleClose}
            aria-label="Close"
            disabled={submitting}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content */}
        <div className="link-existing-content">
          <div className="link-existing-info-box">
            <FontAwesomeIcon icon={faInfoCircle} className="link-existing-info-icon" />
            <span>
              Linking sets each selected person's company to{' '}
              <strong>{companyName || '—'}</strong>. If they were previously at another
              organization, they will be moved.
            </span>
          </div>

          <div className="link-existing-search-wrapper">
            <FontAwesomeIcon icon={faSearch} className="link-existing-search-icon" />
            <input
              type="text"
              className="link-existing-search-input"
              placeholder="Search by name, email, title, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={loading || submitting}
            />
          </div>

          <div className="link-existing-toolbar">
            <span>
              <span className="link-existing-selected-count">{selectedCount}</span> selected
              {' · '}
              {filteredCandidates.length} match{filteredCandidates.length === 1 ? '' : 'es'}
            </span>
            <button
              type="button"
              className="link-existing-toolbar-button"
              onClick={toggleAllFiltered}
              disabled={loading || submitting || filteredCandidates.length === 0}
            >
              {allFilteredSelected ? 'Clear selection' : 'Select all'}
            </button>
          </div>

          <div className="link-existing-list" role="listbox" aria-multiselectable="true">
            {loading ? (
              <div className="link-existing-loading">
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>Loading contacts...</span>
              </div>
            ) : filteredCandidates.length === 0 ? (
              <div className="link-existing-empty">
                {candidates.length === 0
                  ? 'No other contacts available to link.'
                  : 'No contacts match your search.'}
              </div>
            ) : (
              filteredCandidates.map((c) => {
                const key = `${c.source}:${c.id}`;
                const checked = selectedIds.has(key);
                return (
                  <div
                    key={key}
                    className={`link-existing-row ${checked ? 'selected' : ''}`}
                    onClick={() => toggleOne(key)}
                    role="option"
                    aria-selected={checked}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleOne(key);
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      className="link-existing-checkbox"
                      checked={checked}
                      onChange={() => toggleOne(key)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select ${c.fullName}`}
                    />
                    <div className="link-existing-avatar">
                      {getInitials(c.fullName || c.email || '?')}
                    </div>
                    <div className="link-existing-row-info">
                      <div className="link-existing-row-name">
                        <span>{c.fullName || c.email || c.id}</span>
                        <span
                          className={`link-existing-source-badge ${c.source === 'client' ? 'client' : 'agent'}`}
                        >
                          {c.source === 'client' ? 'Client' : 'Other'}
                        </span>
                      </div>
                      <div className="link-existing-row-meta">
                        {c.title || 'No title'}
                        {c.email ? ` · ${c.email}` : ''}
                      </div>
                      {c.currentCompany && (
                        <span className="link-existing-current-company">
                          <FontAwesomeIcon icon={faBuilding} />
                          Currently at {c.currentCompany}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {error && <div className="link-existing-error">{error}</div>}
        </div>

        {/* Footer */}
        <div className="link-existing-footer">
          <div className="link-existing-footer-info">
            <FontAwesomeIcon icon={faInfoCircle} />
            <span>
              {selectedCount === 0
                ? 'Select one or more existing contacts to continue.'
                : `${selectedCount} contact${selectedCount === 1 ? '' : 's'} will be linked to ${companyName || 'this organization'}.`}
            </span>
          </div>
          <div className="link-existing-footer-buttons">
            <button
              type="button"
              className="link-existing-button link-existing-button-cancel"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="link-existing-button link-existing-button-primary"
              onClick={handleSubmit}
              disabled={submitting || selectedCount === 0 || loading}
            >
              {submitting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Linking...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  Link {selectedCount > 0 ? selectedCount : ''} Contact{selectedCount === 1 ? '' : 's'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkExistingContactModal;
